import React, { useState, useEffect } from 'react';
import { 
    Home, FileText, CreditCard, Wrench, MessageSquare, 
    Phone, MapPin, Clock, Calendar, CheckCircle,
    AlertCircle, Download, User, Settings, ChevronRight,
    LogOut, Building2, Mail, DollarSign, Shield,
    Menu, X, Smartphone, Loader, Check
} from 'lucide-react';

import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';

const TenantPortal = () => {
    const { user, logout } = useAuth();
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState('dashboard');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [portalData, setPortalData] = useState({
        lease: null,
        payments: [],
        maintenance: [],
        unreadMessages: 0
    });
    const [contactForm, setContactForm] = useState({ subject: '', message: '' });
    const [sendingMessage, setSendingMessage] = useState(false);
    
    // Contacts
    const [contacts, setContacts] = useState([]);
    const [loadingContacts, setLoadingContacts] = useState(true);
    
    // M-Pesa payment state
    const [mpesaModal, setMpesaModal] = useState({ open: false, payment: null });
    const [phoneNumber, setPhoneNumber] = useState(user?.phone || '');
    const [mpesaProcessing, setMpesaProcessing] = useState(false);
    const [mpesaStatus, setMpesaStatus] = useState(null); // null | 'sent' | 'success' | 'error'
    const [mpesaMessage, setMpesaMessage] = useState('');
    
    // M-Pesa payment info (Paybill/Till numbers)
    const [mpesaInfo, setMpesaInfo] = useState(null);
    
    // Manual payment recording
    const [recordModal, setRecordModal] = useState({ open: false });
    const [recordForm, setRecordForm] = useState({ amount: '', receipt: '', phone: '' });
    const [recordProcessing, setRecordProcessing] = useState(false);
    const [recordSuccess, setRecordSuccess] = useState(null);

    useEffect(() => {
        fetchPortalData();
        fetchMpesaInfo();
    }, []);
    
    const fetchMpesaInfo = async () => {
        try {
            const res = await api.get('/payments/mpesa/info');
            setMpesaInfo(res.data?.data || null);
        } catch (e) {
            // No M-Pesa info available
        }
    };

    const fetchPortalData = async () => {
        try {
            const [leaseRes, paymentsRes, maintenanceRes, messagesRes, contactsRes] = await Promise.all([
                api.get('/leases?limit=1').catch(() => null),
                api.get('/payments?limit=10').catch(() => null),
                api.get('/maintenance?limit=10').catch(() => null),
                api.get('/chat/conversations/unread').catch(() => null),
                api.get('/chat/contacts').catch(() => null)
            ]);

            setPortalData({
                lease: leaseRes?.data?.data?.data?.[0] || null,
                payments: paymentsRes?.data?.data?.data || [],
                maintenance: maintenanceRes?.data?.data?.data || [],
                unreadMessages: messagesRes?.data?.data?.unread_count || 0
            });
            
            if (contactsRes?.data?.data) {
                setContacts(contactsRes.data.data);
            }
        } catch (error) {
            console.error('Error fetching portal data:', error);
        } finally {
            setLoading(false);
            setLoadingContacts(false);
        }
    };

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        if (!contactForm.message.trim()) return;
        
        setSendingMessage(true);
        try {
            // Find or create a conversation with landlord/caretaker
            const contactsRes = await api.get('/chat/contacts');
            const contacts = contactsRes.data.data || [];
            const landlordOrCaretaker = contacts.find(c => 
                c.role === 'landlord' || c.role === 'caretaker'
            );
            
            if (landlordOrCaretaker) {
                const convRes = await api.post('/chat/direct', { user_id: landlordOrCaretaker.id });
                const conversation = convRes.data.data;
                
                await api.post(`/chat/conversations/${conversation.id}/messages`, {
                    message: `${contactForm.subject ? `[${contactForm.subject}] ` : ''}${contactForm.message}`
                });
                
                alert('Message sent successfully!');
                setContactForm({ subject: '', message: '' });
            } else {
                alert('No landlord or caretaker available to contact.');
            }
        } catch (error) {
            console.error('Error sending message:', error);
            alert('Failed to send message. Please try again.');
        } finally {
            setSendingMessage(false);
        }
    };

    const calculateLeaseProgress = () => {
        if (!portalData.lease) return 0;
        const start = new Date(portalData.lease.start_date);
        const end = new Date(portalData.lease.end_date);
        const now = new Date();
        const total = end - start;
        const elapsed = now - start;
        return Math.min(100, Math.max(0, (elapsed / total) * 100));
    };

    const getDaysUntilDue = () => {
        if (!portalData.lease) return null;
        const dueDay = portalData.lease.payment_due_day || 1;
        const now = new Date();
        const dueDate = new Date(now.getFullYear(), now.getMonth(), dueDay);
        
        // Check if this month's rent has been paid already
        const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        
        const paidThisMonth = portalData.payments.some(p => 
            p.status === 'completed' && 
            new Date(p.created_at) >= thisMonthStart &&
            new Date(p.created_at) <= thisMonthEnd
        );
        
        if (paidThisMonth) {
            // Already paid for this month, show days until next due date
            const nextDueDate = new Date(now.getFullYear(), now.getMonth() + 1, dueDay);
            return Math.ceil((nextDueDate - now) / (1000 * 60 * 60 * 24));
        }
        
        // Not yet paid for this month
        if (dueDate < now) {
            // Past due date - overdue (return negative number)
            const overdueDays = Math.ceil((now - dueDate) / (1000 * 60 * 60 * 24));
            return -overdueDays; // negative = overdue
        }
        
        // Before due date, not paid yet
        return Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
    };


    const getActiveMaintenance = () => 
        portalData.maintenance.filter(m => m.status !== 'resolved' && m.status !== 'cancelled');
    
    const getResolvedMaintenance = () =>
        portalData.maintenance.filter(m => m.status === 'resolved' || m.status === 'cancelled');
    
    const calculateOutstandingBalance = () => {
        if (!portalData.lease) return 0;
        
        const monthlyRent = parseFloat(portalData.lease.rent_amount || portalData.lease.effective_rate || 0);
        
        // Get total completed payments for the current month
        const now = new Date();
        const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        
        const totalPaidThisMonth = portalData.payments
            .filter(p => 
                p.status === 'completed' && 
                p.payment_type === 'rent' &&
                new Date(p.created_at) >= thisMonthStart &&
                new Date(p.created_at) <= thisMonthEnd
            )
            .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
        
        const outstanding = monthlyRent - totalPaidThisMonth;
        return Math.max(0, outstanding); // Never negative
    };

    const daysUntilDue = getDaysUntilDue();
    const leaseProgress = calculateLeaseProgress();
    const activeMaintenance = getActiveMaintenance();
    const resolvedMaintenance = getResolvedMaintenance();

    if (loading) return <LoadingSpinner size="lg" />;

    // Navigation sections for the portal
    const navSections = [
        { id: 'dashboard', icon: Home, label: 'Dashboard' },
        { id: 'lease', icon: FileText, label: 'My Lease' },
        { id: 'payments', icon: CreditCard, label: 'Payments' },
        { id: 'maintenance', icon: Wrench, label: 'Maintenance' },
        { id: 'messages', icon: MessageSquare, label: 'Messages', badge: portalData.unreadMessages },
        { id: 'documents', icon: Download, label: 'Documents' },
        { id: 'property', icon: Building2, label: 'Property Info' },
        { id: 'account', icon: User, label: 'Account' },
    ];

    const renderDashboard = () => (
        <div className="space-y-6">
            {/* Welcome Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white">
                <h1 className="text-2xl font-bold">Welcome back, {user?.first_name || 'Tenant'}!</h1>
                <p className="text-indigo-200 mt-1">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                <div className="mt-3 inline-flex items-center px-3 py-1 bg-white bg-opacity-20 rounded-full text-sm">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Your account is in good standing
                </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-4 shadow-sm border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs text-gray-500">Payment Due</p>
                            <p className={`text-2xl font-bold mt-1 ${daysUntilDue !== null && daysUntilDue < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                                {portalData.lease ? 
                                    daysUntilDue !== null ? 
                                        daysUntilDue < 0 ? `${Math.abs(daysUntilDue)}d overdue` : `${daysUntilDue}d`
                                    : 'N/A' 
                                : 'N/A'}
                            </p>
                        </div>
                        <div className={`p-2 rounded-lg ${daysUntilDue !== null && daysUntilDue < 0 ? 'bg-red-100' : 'bg-blue-100'}`}>
                            <Calendar className={`h-5 w-5 ${daysUntilDue !== null && daysUntilDue < 0 ? 'text-red-600' : 'text-blue-600'}`} />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-4 shadow-sm border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs text-gray-500">Total Paid This Year</p>
                            <p className="text-2xl font-bold text-green-600 mt-1">
                                KES {portalData.payments
                                    .filter(p => p.status === 'completed')
                                    .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)
                                    .toLocaleString()}
                            </p>
                        </div>
                        <div className="bg-green-100 p-2 rounded-lg">
                            <DollarSign className="h-5 w-5 text-green-600" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs text-gray-500">Maintenance Tickets</p>
                            <p className="text-2xl font-bold text-gray-900 mt-1">{activeMaintenance.length}</p>
                        </div>
                        <div className="bg-orange-100 p-2 rounded-lg">
                            <Wrench className="h-5 w-5 text-orange-600" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs text-gray-500">Unread Messages</p>
                            <p className="text-2xl font-bold text-purple-600 mt-1">{portalData.unreadMessages}</p>
                        </div>
                        <div className="bg-purple-100 p-2 rounded-lg">
                            <MessageSquare className="h-5 w-5 text-purple-600" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Current Lease Summary */}
            {portalData.lease && (
                <div className="bg-white rounded-xl shadow-sm border p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Lease Summary</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-3">
                            <div>
                                <p className="text-sm text-gray-500">Property</p>
                                <p className="font-medium">{portalData.lease.asset_name} - Unit {portalData.lease.unit_number}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Monthly Rent</p>
                                <p className="font-medium text-lg">KES {parseFloat(portalData.lease.rent_amount || portalData.lease.effective_rate).toLocaleString()}</p>
                            </div>
                            <div className="flex items-center">
                                <Calendar className={`h-4 w-4 mr-2 ${daysUntilDue !== null && daysUntilDue < 0 ? 'text-red-500' : 'text-gray-400'}`} />
                                <span className={`text-sm ${daysUntilDue !== null && daysUntilDue < 0 ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                                    {daysUntilDue !== null ? 
                                        daysUntilDue < 0 ? 
                                            `Payment overdue by ${Math.abs(daysUntilDue)} days` 
                                            : `Next payment due in ${daysUntilDue} days`
                                        : 'N/A'
                                    }
                                </span>
                            </div>

                        </div>
                        <div className="space-y-3">
                            <div>
                                <p className="text-sm text-gray-500">Lease Period</p>
                                <p className="font-medium">
                                    {new Date(portalData.lease.start_date).toLocaleDateString()} - {new Date(portalData.lease.end_date).toLocaleDateString()}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Lease Progress</p>
                                <div className="mt-1">
                                    <div className="flex justify-between text-sm mb-1">
                                        <span>Month {Math.round((leaseProgress / 100) * 12)} of 12</span>
                                        <span>{Math.round(leaseProgress)}%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div 
                                            className="bg-indigo-600 h-2 rounded-full transition-all duration-500"
                                            style={{ width: `${leaseProgress}%` }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Payment & Maintenance Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Payment Section */}
                <div className="bg-white rounded-xl shadow-sm border p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Payments</h3>
                    {portalData.payments.length > 0 ? (
                        <div className="space-y-3">
                            {portalData.payments.slice(0, 5).map(payment => (
                                <div key={payment.id} className="flex items-center justify-between py-2 border-b last:border-0">
                                    <div>
                                        <p className="font-medium text-sm">KES {parseFloat(payment.amount).toLocaleString()}</p>
                                        <p className="text-xs text-gray-500">{new Date(payment.created_at).toLocaleDateString()}</p>
                                    </div>
                                    <span className={`text-xs px-2 py-1 rounded-full ${
                                        payment.status === 'completed' ? 'bg-green-100 text-green-700' :
                                        payment.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-red-100 text-red-700'
                                    }`}>
                                        {payment.status}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500 text-sm">No payment history available</p>
                    )}
                    <a href="#payments" onClick={() => setActiveSection('payments')} className="mt-3 inline-flex items-center text-sm text-indigo-600 hover:text-indigo-800">
                        View All Payments <ChevronRight className="h-4 w-4 ml-1" />
                    </a>
                </div>

                {/* Active Maintenance */}
                <div className="bg-white rounded-xl shadow-sm border p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Maintenance</h3>
                    {activeMaintenance.length > 0 ? (
                        <div className="space-y-3">
                            {activeMaintenance.slice(0, 5).map(ticket => (
                                <div key={ticket.id} className="flex items-center justify-between py-2 border-b last:border-0">
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-sm truncate">{ticket.title}</p>
                                        <p className="text-xs text-gray-500">{new Date(ticket.created_at).toLocaleDateString()}</p>
                                    </div>
                                    <span className={`text-xs px-2 py-1 rounded-full ml-2 ${
                                        ticket.status === 'open' ? 'bg-red-100 text-red-700' :
                                        ticket.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-green-100 text-green-700'
                                    }`}>
                                        {ticket.status}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500 text-sm">No active maintenance requests</p>
                    )}
                    <a href="#maintenance" onClick={() => setActiveSection('maintenance')} className="mt-3 inline-flex items-center text-sm text-indigo-600 hover:text-indigo-800">
                        Manage Maintenance <ChevronRight className="h-4 w-4 ml-1" />
                    </a>
                </div>
            </div>
        </div>
    );

    const handleAdvancePayNow = () => {

        // Check if there is a pending payment already
        const pendingPayment = portalData.payments.find(p => p.status === 'pending');
        if (pendingPayment) {
            // Use existing pending payment
            setMpesaModal({ open: true, payment: pendingPayment });
        } else {
            // Create a virtual payment object from lease data for the modal
            // The backend will create the actual payment record when we initiate
            const lease = portalData.lease;
            if (!lease) return;
            
            const virtualPayment = {
                id: null, // Will be created on the backend
                lease_id: lease.id,
                amount: parseFloat(lease.rent_amount || lease.effective_rate || 0),
                payment_type: 'rent',
                status: 'pending',
                virtual: true // Flag to indicate this needs advance endpoint
            };
            setMpesaModal({ open: true, payment: virtualPayment });
        }
        setPhoneNumber(user?.phone || '');
        setMpesaStatus(null);
        setMpesaMessage('');
    };

    const handleOpenRecordModal = () => {
        setRecordForm({
            amount: portalData.lease?.rent_amount || '',
            receipt: '',
            phone: user?.phone || ''
        });
        setRecordSuccess(null);
        setRecordModal({ open: true });
    };

    const handleRecordPayment = async () => {
        if (!recordForm.receipt.trim() || !recordForm.amount) return;
        
        setRecordProcessing(true);
        setRecordSuccess(null);
        
        try {
            const res = await api.post('/payments/manual/record', {
                lease_id: portalData.lease.id,
                amount: parseFloat(recordForm.amount),
                mpesa_receipt_number: recordForm.receipt.trim().toUpperCase(),
                phone_number: recordForm.phone,
                payment_type: 'rent'
            });
            
            setRecordSuccess({
                type: 'success',
                message: `Payment of KES ${parseFloat(recordForm.amount).toLocaleString()} recorded successfully!`
            });
            setRecordForm({ amount: '', receipt: '', phone: '' });
            fetchPortalData(); // Refresh data
        } catch (error) {
            setRecordSuccess({
                type: 'error',
                message: error.response?.data?.error || 'Failed to record payment. Please try again.'
            });
        } finally {
            setRecordProcessing(false);
        }
    };

    const handleMpesaPay = async () => {
        if (!mpesaModal.payment || !phoneNumber.trim()) return;
        
        setMpesaProcessing(true);
        setMpesaStatus(null);
        setMpesaMessage('');
        
        try {
            // If this is an advance payment (no existing payment ID), use the advance endpoint
            if (mpesaModal.payment.virtual) {
                const res = await api.post('/payments/advance/initiate', {
                    phoneNumber: phoneNumber.replace(/\s/g, '')
                });
                
                const data = res.data?.data || {};
                setMpesaStatus('sent');
                setMpesaMessage('STK Push sent to your phone. Please check M-Pesa and enter PIN.');
                
                // Poll for payment status using the returned payment_id
                const paymentId = data.payment_id;
                if (paymentId) {
                    const checkStatus = setInterval(async () => {
                        try {
                            const updatedPayment = await api.get(`/payments/${paymentId}`);
                            if (updatedPayment.data?.data?.status === 'completed') {
                                setMpesaStatus('success');
                                setMpesaMessage('Payment successful!');
                                clearInterval(checkStatus);
                                fetchPortalData();
                            } else if (updatedPayment.data?.data?.status === 'failed') {
                                setMpesaStatus('error');
                                setMpesaMessage('Payment failed. Please try again.');
                                clearInterval(checkStatus);
                            }
                        } catch (e) {
                            // Ignore polling errors
                        }
                    }, 3000);
                    
                    setTimeout(() => clearInterval(checkStatus), 180000);
                }
            } else {
                const res = await api.post(`/payments/${mpesaModal.payment.id}/mpesa/initiate`, {
                    phoneNumber: phoneNumber.replace(/\s/g, '')
                });
                
                setMpesaStatus('sent');
                setMpesaMessage('STK Push sent to your phone. Please check M-Pesa and enter PIN.');
                
                // Poll for payment status
                const checkStatus = setInterval(async () => {
                    try {
                        const updatedPayment = await api.get(`/payments/${mpesaModal.payment.id}`);
                        if (updatedPayment.data?.data?.status === 'completed') {
                            setMpesaStatus('success');
                            setMpesaMessage('Payment successful!');
                            clearInterval(checkStatus);
                            fetchPortalData();
                        } else if (updatedPayment.data?.data?.status === 'failed') {
                            setMpesaStatus('error');
                            setMpesaMessage('Payment failed. Please try again.');
                            clearInterval(checkStatus);
                        }
                    } catch (e) {
                        // Ignore polling errors
                    }
                }, 3000);
                
                // Stop polling after 3 minutes
                setTimeout(() => clearInterval(checkStatus), 180000);
            }
        } catch (error) {
            setMpesaStatus('error');
            setMpesaMessage(error.response?.data?.error || 'Failed to initiate payment. Please try again.');
        } finally {
            setMpesaProcessing(false);
        }
    };


    const renderPayments = () => (
        <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Payment History</h2>
            
            {/* Current Balance */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <p className="text-sm text-gray-500">Current Balance</p>
                        <p className={`text-3xl font-bold mt-1 ${
                            (calculateOutstandingBalance() > 0) ? 'text-red-600' : 'text-green-600'
                        }`}>
                            KES {calculateOutstandingBalance().toLocaleString()}
                        </p>
                        {calculateOutstandingBalance() > 0 && (
                            <p className="text-xs text-red-500 mt-1">Outstanding balance for this month</p>
                        )}
                        {calculateOutstandingBalance() <= 0 && (
                            <p className="text-xs text-green-500 mt-1">All paid up for this month</p>
                        )}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                        {portalData.lease && portalData.lease.status === 'active' && (
                            <>
                                <button 
                                    onClick={handleAdvancePayNow}
                                    className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center"
                                >
                                    <CreditCard className="h-5 w-5 mr-2" />
                                    Pay Now (STK)
                                </button>
                                <button 
                                    onClick={handleOpenRecordModal}
                                    className="px-6 py-3 border-2 border-green-600 text-green-700 rounded-lg hover:bg-green-50 flex items-center"
                                >
                                    <Smartphone className="h-5 w-5 mr-2" />
                                    Record M-Pesa
                                </button>
                            </>
                        )}
                    </div>

                </div>
                {portalData.payments.filter(p => p.status === 'completed').length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                        <p className="text-sm text-gray-500">Last Payment: 
                            <span className="font-medium text-gray-700 ml-1">
                                KES {portalData.payments
                                    .filter(p => p.status === 'completed')
                                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]?.amount || 0}
                            </span>
                            <span className="text-gray-400 ml-2">
                                on {new Date(portalData.payments
                                    .filter(p => p.status === 'completed')
                                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]?.created_at).toLocaleDateString()}
                            </span>
                        </p>
                    </div>
                )}
            </div>

            {/* M-Pesa Paybill / Till Info Card */}
            {mpesaInfo && (mpesaInfo.paybill_number || mpesaInfo.till_number) && (
                <div className="bg-gradient-to-r from-green-50 to-teal-50 border border-green-200 rounded-xl p-5">
                    <div className="flex items-start">
                        <Smartphone className="h-6 w-6 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-2">M-Pesa Payment Instructions</h4>
                            <p className="text-sm text-gray-600 mb-3">
                                For manual M-Pesa payments, send your rent to the details below, then click 
                                <strong> "Record M-Pesa"</strong> to submit your receipt number.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {mpesaInfo.paybill_number && (
                                    <div className="bg-white rounded-lg p-3 border border-green-100">
                                        <p className="text-xs text-gray-500 uppercase tracking-wide">Paybill</p>
                                        <p className="text-xl font-bold text-green-700">{mpesaInfo.paybill_number}</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Account: <span className="font-mono font-medium">{portalData.lease?.lease_number || 'Your Lease #'}</span>
                                        </p>
                                    </div>
                                )}
                                {mpesaInfo.till_number && (
                                    <div className="bg-white rounded-lg p-3 border border-teal-100">
                                        <p className="text-xs text-gray-500 uppercase tracking-wide">Till Number</p>
                                        <p className="text-xl font-bold text-teal-700">{mpesaInfo.till_number}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Payment Table */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <div className="p-4 border-b bg-gray-50">
                    <h3 className="font-semibold text-gray-900">Payment History</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b bg-gray-50">
                                <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                                <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Amount</th>
                                <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Type</th>
                                <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Receipt</th>
                            </tr>
                        </thead>
                        <tbody>
                            {portalData.payments.length > 0 ? portalData.payments.map(payment => (
                                <tr key={payment.id} className="border-b hover:bg-gray-50">
                                    <td className="p-3 text-sm">{new Date(payment.created_at).toLocaleDateString()}</td>
                                    <td className="p-3 text-sm font-medium">KES {parseFloat(payment.amount).toLocaleString()}</td>
                                    <td className="p-3 text-sm capitalize">{payment.payment_type || 'rent'}</td>
                                    <td className="p-3">
                                        <span className={`text-xs px-2 py-1 rounded-full ${
                                            payment.status === 'completed' ? 'bg-green-100 text-green-700' :
                                            payment.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                            'bg-red-100 text-red-700'
                                        }`}>
                                            {payment.status}
                                        </span>
                                    </td>
                                    <td className="p-3">
                                        {payment.status === 'completed' && (
                                            <button 
                                                onClick={async () => {
                                                    try {
                                                        const res = await api.get(`/payments/${payment.id}/receipt`, { responseType: 'blob' });
                                                        const url = window.URL.createObjectURL(new Blob([res.data]));
                                                        const a = document.createElement('a');
                                                        a.href = url;
                                                        a.download = `receipt_${payment.receipt_number || payment.id}.pdf`;
                                                        document.body.appendChild(a);
                                                        a.click();
                                                        a.remove();
                                                        window.URL.revokeObjectURL(url);
                                                    } catch (err) {
                                                        console.error('Error downloading receipt:', err);
                                                        alert('Failed to download receipt');
                                                    }
                                                }}
                                                className="text-indigo-600 hover:text-indigo-800 text-sm flex items-center"
                                            >
                                                <Download className="h-4 w-4 mr-1" /> PDF
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" className="p-6 text-center text-gray-500">No payments found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const renderMaintenance = () => {
        // Build WhatsApp URL from contacts (loaded from API)
        const maintenanceContact = contacts.find(c => c.role === 'landlord') || contacts.find(c => c.role === 'caretaker');
        let whatsappUrl = null;
        if (maintenanceContact?.phone) {
            const whatsappNumber = maintenanceContact.phone.replace(/[\s\-\+]/g, '');
            const unitInfo = portalData.lease ? `Unit ${portalData.lease.unit_number}, ${portalData.lease.asset_name}` : 'my unit';
            const whatsappMsg = encodeURIComponent(`Hi, I need maintenance repair at ${unitInfo}. Please advise.`);
            whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMsg}`;
        }

        return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Maintenance Requests</h2>
                {loadingContacts ? (
                    <div className="px-4 py-2 bg-gray-400 text-white rounded-lg flex items-center cursor-not-allowed">
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                        Loading contacts...
                    </div>
                ) : whatsappUrl ? (
                    <a 
                        href={whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
                    >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Request via WhatsApp
                    </a>
                ) : null}
            </div>

            {/* Active Requests */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <div className="p-4 border-b bg-gray-50">
                    <h3 className="font-semibold text-gray-900">Active Requests ({activeMaintenance.length})</h3>
                </div>
                <div className="divide-y">
                    {activeMaintenance.length > 0 ? activeMaintenance.map(ticket => (
                        <div key={ticket.id} className="p-4 hover:bg-gray-50">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium text-gray-900">{ticket.title}</p>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Ticket #{ticket.id} • {new Date(ticket.created_at).toLocaleDateString()}
                                    </p>
                                    {ticket.assigned_to && (
                                        <p className="text-sm text-gray-500 flex items-center mt-1">
                                            <User className="h-3 w-3 mr-1" />
                                            Assigned: Technician
                                        </p>
                                    )}
                                </div>
                                <span className={`text-xs px-3 py-1 rounded-full ${
                                    ticket.status === 'open' ? 'bg-red-100 text-red-700' :
                                    ticket.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-green-100 text-green-700'
                                }`}>
                                    {ticket.status.replace('_', ' ')}
                                </span>
                            </div>
                        </div>
                    )) : (
                        <div className="p-6 text-center text-gray-500">No active maintenance requests</div>
                    )}
                </div>
            </div>

            {/* Resolved Requests */}
            {resolvedMaintenance.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                    <div className="p-4 border-b bg-gray-50">
                        <h3 className="font-semibold text-gray-900">Request History ({resolvedMaintenance.length})</h3>
                    </div>
                    <div className="divide-y">
                        {resolvedMaintenance.map(ticket => (
                            <div key={ticket.id} className="p-4 hover:bg-gray-50">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-medium text-gray-900">{ticket.title}</p>
                                        <p className="text-sm text-gray-500">Resolved on {new Date(ticket.completed_date || ticket.updated_at).toLocaleDateString()}</p>
                                    </div>
                                    <span className="text-xs px-3 py-1 rounded-full bg-green-100 text-green-700">
                                        {ticket.status}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
    };

    const renderMessages = () => (
        <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Contact Landlord</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Quick Message Form */}
                <div className="bg-white rounded-xl shadow-sm border p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Send a Message</h3>
                    <form onSubmit={handleContactSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                            <select
                                value={contactForm.subject}
                                onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 p-2 focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="">General Inquiry</option>
                                <option value="Maintenance">Maintenance Issue</option>
                                <option value="Payment">Payment Question</option>
                                <option value="Lease">Lease Question</option>
                                <option value="Emergency">Emergency</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                            <textarea
                                value={contactForm.message}
                                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                                rows={4}
                                className="w-full rounded-lg border border-gray-300 p-2 focus:ring-2 focus:ring-indigo-500"
                                placeholder="Type your message here..."
                                required
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={sendingMessage || !contactForm.message.trim()}
                            className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                        >
                            {sendingMessage ? 'Sending...' : 'Send Message'}
                        </button>
                    </form>
                </div>

                {/* Quick info */}
                <div className="bg-white rounded-xl shadow-sm border p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                    
                    {loadingContacts ? (
                        <div className="flex items-center justify-center py-4">
                            <Loader className="h-5 w-5 text-gray-400 animate-spin" />
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {contacts.filter(c => c.role === 'caretaker').slice(0, 1).map(caretaker => (
                                <div key={caretaker.id} className="flex items-start">
                                    <Phone className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                                    <div>
                                        <p className="font-medium">Caretaker</p>
                                        <p className="text-sm text-gray-500">{caretaker.phone || 'N/A'}</p>
                                        <p className="text-xs text-gray-400">{caretaker.first_name} {caretaker.last_name}</p>
                                    </div>
                                </div>
                            ))}
                            {contacts.filter(c => c.role === 'landlord').slice(0, 1).map(landlord => (
                                <div key={landlord.id} className="flex items-start">
                                    <Mail className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                                    <div>
                                        <p className="font-medium">Property Manager / Landlord</p>
                                        <p className="text-sm text-gray-500">{landlord.phone || landlord.email || 'N/A'}</p>
                                        <p className="text-xs text-gray-400">{landlord.first_name} {landlord.last_name}</p>
                                    </div>
                                </div>
                            ))}
                            {contacts.length === 0 && (
                                <p className="text-sm text-gray-400">Contact information not available</p>
                            )}
                            <div className="flex items-start">
                                <Clock className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                                <div>
                                    <p className="font-medium">Office Hours</p>
                                    <p className="text-sm text-gray-500">Mon - Fri: 8:00 AM - 5:00 PM</p>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="mt-6 p-4 bg-orange-50 rounded-lg">
                        <div className="flex items-start">
                            <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5 mr-2" />
                            <p className="text-sm text-orange-700">
                                For emergencies, please call the emergency number directly. Message responses may take up to 24 hours.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderDocuments = () => (
        <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Documents</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                    { title: 'Lease Agreement', desc: 'Signed lease contract', icon: FileText, color: 'text-blue-600', bg: 'bg-blue-100' },
                    { title: 'Payment Receipts', desc: 'All payment receipts', icon: Download, color: 'text-green-600', bg: 'bg-green-100' },
                    { title: 'House Rules', desc: 'Property guidelines', icon: Shield, color: 'text-purple-600', bg: 'bg-purple-100' },
                    { title: 'Inspection Reports', desc: 'Move-in/Move-out reports', icon: FileText, color: 'text-orange-600', bg: 'bg-orange-100' },
                ].map((doc, i) => (
                    <div key={i} className="bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow cursor-pointer">
                        <div className="flex items-start">
                            <div className={`${doc.bg} p-3 rounded-lg`}>
                                <doc.icon className={`h-6 w-6 ${doc.color}`} />
                            </div>
                            <div className="ml-4">
                                <h3 className="font-semibold text-gray-900">{doc.title}</h3>
                                <p className="text-sm text-gray-500 mt-1">{doc.desc}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderPropertyInfo = () => (
        <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Property Information</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl shadow-sm border p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Address & Location</h3>
                    {portalData.lease ? (
                        <div className="space-y-3">
                            <p className="font-medium">{portalData.lease.asset_name}</p>
                            <p className="text-sm text-gray-500">Unit: {portalData.lease.unit_number}</p>
                        </div>
                    ) : (
                        <p className="text-gray-500">Property details not available</p>
                    )}
                </div>
                <div className="bg-white rounded-xl shadow-sm border p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Emergency Contacts</h3>
                    
                    {loadingContacts ? (
                        <div className="flex items-center justify-center py-4">
                            <Loader className="h-5 w-5 text-gray-400 animate-spin" />
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {/* Caretaker(s) */}
                            {contacts.filter(c => c.role === 'caretaker').length > 0 ? contacts.filter(c => c.role === 'caretaker').map(caretaker => (
                                <div key={caretaker.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                                    <div className="flex items-center">
                                        <Phone className="h-4 w-4 text-blue-600 mr-2" />
                                        <div>
                                            <span className="text-sm font-medium">Caretaker</span>
                                            {caretaker.first_name && (
                                                <p className="text-xs text-gray-500">{caretaker.first_name} {caretaker.last_name}</p>
                                            )}
                                        </div>
                                    </div>
                                    <span className="text-sm font-medium">{caretaker.phone || 'N/A'}</span>
                                </div>
                            )) : (
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <span className="text-sm text-gray-500">No caretaker assigned</span>
                                </div>
                            )}

                            {/* Landlord(s) as Emergency */}
                            {contacts.filter(c => c.role === 'landlord').length > 0 && contacts.filter(c => c.role === 'landlord').map(landlord => (
                                <div key={landlord.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                                    <div className="flex items-center">
                                        <AlertCircle className="h-4 w-4 text-red-600 mr-2" />
                                        <div>
                                            <span className="text-sm font-medium">Landlord (Emergency)</span>
                                            {landlord.first_name && (
                                                <p className="text-xs text-gray-500">{landlord.first_name} {landlord.last_name}</p>
                                            )}
                                        </div>
                                    </div>
                                    <span className="text-sm font-medium">{landlord.phone || 'N/A'}</span>
                                </div>
                            ))}

                            {/* National Emergency */}
                            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                                <div className="flex items-center">
                                    <Phone className="h-4 w-4 text-yellow-600 mr-2" />
                                    <span className="text-sm font-medium">Emergency Services</span>
                                </div>
                                <span className="text-sm font-medium">999 / 112</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderAccount = () => (
        <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Account Settings</h2>
            <div className="bg-white rounded-xl shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile</h3>
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">First Name</label>
                            <p className="mt-1 text-gray-900">{user?.first_name || 'N/A'}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Last Name</label>
                            <p className="mt-1 text-gray-900">{user?.last_name || 'N/A'}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Email</label>
                            <p className="mt-1 text-gray-900">{user?.email || 'N/A'}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Phone</label>
                            <p className="mt-1 text-gray-900">{user?.phone || 'N/A'}</p>
                        </div>
                    </div>
                </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification Preferences</h3>
                <div className="space-y-3">
                    {[
                        { label: 'SMS Reminders', desc: 'Receive payment reminders via SMS' },
                        { label: 'Email Notifications', desc: 'Get notified about maintenance updates' },
                        { label: 'Payment Confirmations', desc: 'Receive confirmation when payments are processed' },
                    ].map((pref, i) => (
                        <div key={i} className="flex items-center justify-between py-2">
                            <div>
                                <p className="font-medium text-sm">{pref.label}</p>
                                <p className="text-xs text-gray-500">{pref.desc}</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" defaultChecked className="sr-only peer" />
                                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                            </label>
                        </div>
                    ))}
                </div>
            </div>
            <div className="flex justify-end">
                <button
                    onClick={logout}
                    className="flex items-center px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                </button>
            </div>
        </div>
    );

    const renderContent = () => {
        switch (activeSection) {
            case 'dashboard': return renderDashboard();
            case 'lease': return (
                <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-gray-900">My Lease</h2>
                    {portalData.lease ? (
                        <div className="space-y-6">
                            {/* Lease Status Banner */}
                            <div className={`rounded-xl p-4 ${portalData.lease.status === 'active' ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                                <div className="flex items-center">
                                    <div className={`p-2 rounded-lg ${portalData.lease.status === 'active' ? 'bg-green-100' : 'bg-yellow-100'}`}>
                                        <FileText className={`h-5 w-5 ${portalData.lease.status === 'active' ? 'text-green-600' : 'text-yellow-600'}`} />
                                    </div>
                                    <div className="ml-3">
                                        <p className={`font-semibold ${portalData.lease.status === 'active' ? 'text-green-800' : 'text-yellow-800'}`}>
                                            Lease {portalData.lease.status === 'active' ? 'Active' : portalData.lease.status}
                                        </p>
                                        <p className={`text-sm ${portalData.lease.status === 'active' ? 'text-green-600' : 'text-yellow-600'}`}>
                                            {portalData.lease.lease_number}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Lease Details */}
                            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                                <div className="p-4 border-b bg-gray-50">
                                    <h3 className="font-semibold text-gray-900">Lease Details</h3>
                                </div>
                                <div className="p-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-4">
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide">Property</p>
                                                <p className="font-medium text-gray-900">{portalData.lease.asset_name}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide">Unit</p>
                                                <p className="font-medium text-gray-900">{portalData.lease.unit_number}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide">Monthly Rent</p>
                                                <p className="font-bold text-lg text-indigo-600">KES {parseFloat(portalData.lease.rent_amount).toLocaleString()}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide">Deposit Amount</p>
                                                <p className="font-medium text-gray-900">KES {parseFloat(portalData.lease.deposit_amount || 0).toLocaleString()}</p>
                                            </div>
                                        </div>
                                        <div className="space-y-4">
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide">Start Date</p>
                                                <p className="font-medium text-gray-900">{new Date(portalData.lease.start_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide">End Date</p>
                                                <p className="font-medium text-gray-900">{new Date(portalData.lease.end_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide">Payment Due Day</p>
                                                <p className="font-medium text-gray-900">Day {portalData.lease.payment_due_day || 1} of each month</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide">Days Remaining</p>
                                                <p className="font-medium text-gray-900">{portalData.lease.days_remaining || 0} days</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Late Fee Info */}
                            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                                <div className="p-4 border-b bg-gray-50">
                                    <h3 className="font-semibold text-gray-900">Terms</h3>
                                </div>
                                <div className="p-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="flex items-center p-3 bg-orange-50 rounded-lg">
                                            <AlertCircle className="h-5 w-5 text-orange-500 mr-3 flex-shrink-0" />
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">Late Fee</p>
                                                <p className="text-xs text-gray-500">
                                                    {portalData.lease.late_fee_percentage 
                                                        ? `${portalData.lease.late_fee_percentage}% of unpaid rent` 
                                                        : portalData.lease.late_fee_flat 
                                                            ? `KES ${parseFloat(portalData.lease.late_fee_flat).toLocaleString()} flat fee`
                                                            : 'No late fee specified'}
                                                </p>
                                            </div>
                                        </div>
                                        {portalData.lease.is_transferred == 1 && (
                                            <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                                                <FileText className="h-5 w-5 text-blue-500 mr-3 flex-shrink-0" />
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">Transferred Lease</p>
                                                    <p className="text-xs text-gray-500">
                                                        {portalData.lease.transfer_reason === 'tenant_requested' ? 'Tenant requested transfer' : 
                                                         portalData.lease.transfer_reason === 'landlord_initiated' ? 'Landlord initiated transfer' :
                                                         portalData.lease.transfer_reason || 'Lease was transferred'}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Lease Progress Bar */}
                            <div className="bg-white rounded-xl shadow-sm border p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm font-medium text-gray-700">Lease Progress</p>
                                    <p className="text-sm text-gray-500">{Math.round(leaseProgress)}% complete</p>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-3">
                                    <div 
                                        className="bg-indigo-600 h-3 rounded-full transition-all duration-500"
                                        style={{ width: `${leaseProgress}%` }}
                                    ></div>
                                </div>
                                <div className="flex justify-between mt-1 text-xs text-gray-400">
                                    <span>{new Date(portalData.lease.start_date).toLocaleDateString()}</span>
                                    <span>{new Date(portalData.lease.end_date).toLocaleDateString()}</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
                            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">No active lease found</p>
                            <p className="text-sm text-gray-400 mt-1">Contact your landlord for lease information</p>
                        </div>
                    )}
                </div>
            );
            case 'payments': return renderPayments();
            case 'maintenance': return renderMaintenance();
            case 'messages': return renderMessages();
            case 'documents': return renderDocuments();
            case 'property': return renderPropertyInfo();
            case 'account': return renderAccount();
            default: return renderDashboard();
        }
    };

    return (
        <div className="flex h-screen bg-gray-50">
            {/* Mobile menu toggle */}
            <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md"
            >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>

            {/* Sidebar */}
            <div className={`
                fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r transform transition-transform duration-300
                ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                <div className="p-4 border-b">
                    <div className="flex items-center space-x-2">
                        <Building2 className="h-7 w-7 text-indigo-600" />
                        <span className="font-bold text-lg">Tenant Portal</span>
                    </div>
                </div>
                <nav className="p-3 space-y-1">
                    {navSections.map(section => (
                        <button
                            key={section.id}
                            onClick={() => { setActiveSection(section.id); setMobileMenuOpen(false); }}
                            className={`w-full flex items-center px-3 py-2.5 rounded-lg text-sm transition-colors ${
                                activeSection === section.id
                                    ? 'bg-indigo-50 text-indigo-700 font-medium'
                                    : 'text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                            <section.icon className="h-4 w-4 mr-3 flex-shrink-0" />
                            <span>{section.label}</span>
                            {section.badge > 0 && (
                                <span className="ml-auto bg-red-500 text-white text-xs rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
                                    {section.badge}
                                </span>
                            )}
                        </button>
                    ))}
                </nav>
                <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
                    <div className="flex items-center">
                        <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                            {user?.first_name?.[0] || 'U'}
                        </div>
                        <div className="ml-3 flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{user?.first_name} {user?.last_name}</p>
                            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                        </div>
                        <button
                            onClick={logout}
                            className="ml-2 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Sign Out"
                        >
                            <LogOut className="h-4 w-4" />
                        </button>
                    </div>
                </div>

            </div>

            {/* Overlay for mobile */}
            {mobileMenuOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-30 z-30 lg:hidden" onClick={() => setMobileMenuOpen(false)} />
            )}

            {/* Record Manual M-Pesa Payment Modal */}
            {recordModal.open && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                        {/* Header */}
                        <div className="p-4 border-b flex items-center justify-between">
                            <h3 className="font-semibold text-lg text-gray-900">Record M-Pesa Payment</h3>
                            <button 
                                onClick={() => { setRecordModal({ open: false }); setRecordSuccess(null); }}
                                className="p-2 hover:bg-gray-100 rounded-lg"
                            >
                                <X className="h-5 w-5 text-gray-500" />
                            </button>
                        </div>

                        <div className="p-4">
                            {/* Success Message */}
                            {recordSuccess && (
                                <div className={`mb-4 p-4 rounded-lg flex items-start ${
                                    recordSuccess.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
                                }`}>
                                    {recordSuccess.type === 'success' ? (
                                        <CheckCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                                    ) : (
                                        <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                                    )}
                                    <p className="text-sm">{recordSuccess.message}</p>
                                </div>
                            )}

                            {!recordSuccess && (
                                <>
                                    {/* Instructions */}
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                                        <p className="text-sm text-blue-700">
                                            <strong>Already sent M-Pesa?</strong> Enter the M-Pesa confirmation 
                                            message details below to record your payment.
                                        </p>
                                    </div>

                                    {/* Amount */}
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Amount (KES)
                                        </label>
                                        <input
                                            type="number"
                                            value={recordForm.amount}
                                            onChange={(e) => setRecordForm({ ...recordForm, amount: e.target.value })}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                            placeholder="e.g. 15000"
                                            disabled={recordProcessing}
                                        />
                                    </div>

                                    {/* M-Pesa Receipt Number */}
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            M-Pesa Receipt Number <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={recordForm.receipt}
                                            onChange={(e) => setRecordForm({ ...recordForm, receipt: e.target.value })}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 uppercase"
                                            placeholder="e.g. QWE1234567"
                                            disabled={recordProcessing}
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Enter the M-Pesa confirmation code from your SMS
                                        </p>
                                    </div>

                                    {/* Phone Number */}
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Phone Number (optional)
                                        </label>
                                        <input
                                            type="tel"
                                            value={recordForm.phone}
                                            onChange={(e) => setRecordForm({ ...recordForm, phone: e.target.value })}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                            placeholder="2547XXXXXXXX"
                                            disabled={recordProcessing}
                                        />
                                    </div>
                                </>
                            )}

                            {/* Action Buttons */}
                            {!recordSuccess && (
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => { setRecordModal({ open: false }); setRecordSuccess(null); }}
                                        className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                        disabled={recordProcessing}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleRecordPayment}
                                        disabled={recordProcessing || !recordForm.receipt.trim() || !recordForm.amount}
                                        className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
                                    >
                                        {recordProcessing ? (
                                            <>
                                                <Loader className="h-4 w-4 mr-2 animate-spin" />
                                                Recording...
                                            </>
                                        ) : (
                                            <>
                                                <Check className="h-4 w-4 mr-2" />
                                                Record Payment
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}

                            {recordSuccess?.type === 'success' && (
                                <button
                                    onClick={() => { setRecordModal({ open: false }); setRecordSuccess(null); }}
                                    className="w-full px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700"
                                >
                                    Done
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* M-Pesa Payment Modal */}
            {mpesaModal.open && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                        {/* Header */}
                        <div className="p-4 border-b flex items-center justify-between">
                            <h3 className="font-semibold text-lg text-gray-900">Pay with M-Pesa</h3>
                            <button 
                                onClick={() => setMpesaModal({ open: false, payment: null })}
                                className="p-2 hover:bg-gray-100 rounded-lg"
                            >
                                <X className="h-5 w-5 text-gray-500" />
                            </button>
                        </div>

                        {mpesaStatus === 'success' ? (
                            /* Success State */
                            <div className="p-8 text-center">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Check className="h-8 w-8 text-green-600" />
                                </div>
                                <h4 className="text-lg font-semibold text-gray-900 mb-2">Payment Successful!</h4>
                                <p className="text-sm text-gray-500 mb-6">{mpesaMessage}</p>
                                <button
                                    onClick={() => { setMpesaModal({ open: false, payment: null }); setMpesaStatus(null); }}
                                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                                >
                                    Done
                                </button>
                            </div>
                        ) : (
                            <>
                                {/* Payment Details */}
                                <div className="p-4 border-b bg-gray-50">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-500">Amount Due</span>
                                        <span className="text-xl font-bold text-indigo-600">
                                            KES {parseFloat(mpesaModal.payment?.amount || 0).toLocaleString()}
                                        </span>
                                    </div>
                                    {mpesaModal.payment?.payment_type && (
                                        <div className="flex items-center justify-between mt-1">
                                            <span className="text-sm text-gray-500">Type</span>
                                            <span className="text-sm capitalize">{mpesaModal.payment.payment_type}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Phone Number Input */}
                                <div className="p-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        M-Pesa Phone Number
                                    </label>
                                    <div className="relative">
                                        <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <input
                                            type="tel"
                                            value={phoneNumber}
                                            onChange={(e) => setPhoneNumber(e.target.value)}
                                            placeholder="2547XXXXXXXX"
                                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                            disabled={mpesaProcessing}
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Enter your M-Pesa registered phone number (e.g. 254712345678)
                                    </p>
                                </div>

                                {/* Status Message */}
                                {mpesaMessage && (
                                    <div className={`mx-4 p-3 rounded-lg flex items-start ${
                                        mpesaStatus === 'sent' ? 'bg-blue-50 text-blue-700' :
                                        mpesaStatus === 'error' ? 'bg-red-50 text-red-700' :
                                        'bg-gray-50 text-gray-700'
                                    }`}>
                                        {mpesaStatus === 'sent' && <Loader className="h-4 w-4 mr-2 mt-0.5 animate-spin flex-shrink-0" />}
                                        {mpesaStatus === 'error' && <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />}
                                        <p className="text-sm">{mpesaMessage}</p>
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div className="p-4 border-t flex gap-3">
                                    <button
                                        onClick={() => setMpesaModal({ open: false, payment: null })}
                                        className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                        disabled={mpesaProcessing}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleMpesaPay}
                                        disabled={mpesaProcessing || !phoneNumber.trim()}
                                        className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
                                    >
                                        {mpesaProcessing ? (
                                            <>
                                                <Loader className="h-4 w-4 mr-2 animate-spin" />
                                                Sending STK...
                                            </>
                                        ) : (
                                            <>
                                                <Smartphone className="h-4 w-4 mr-2" />
                                                Send STK Push
                                            </>
                                        )}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Main Content */}
            <div className="flex-1 overflow-y-auto">
                <div className="p-4 lg:p-8 pt-16 lg:pt-8 max-w-5xl mx-auto">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

export default TenantPortal;
